<template>
    <HeaderContent title="Program" />
    <ActionContent
        ctaText="Program"
        :filterDate="false"
        :filterModal="false"
        @action="addProgram"
        @search-query="searchData"
    />
    <ActivityForm
        :modal="modal"
        :selectedData="selectedData"
        @update:modal="updateModal"
        @addOrUpdate="getAll"
    ></ActivityForm>

    <DataTable
        :data="programList"
        :header="headers"
        :custom-actions="customActions"
    >
    </DataTable>
</template>

<script setup>
import axios from "axios";
import HeaderContent from "@/components/HeaderContent.vue";
import ActionContent from "@/components/ActionContent.vue";
import DataTable from "@/components/DataTable.vue";
import { ref, onMounted, inject } from "vue";
import { useLoading } from "vue-loading-overlay";
import { useAuthStore } from "@/store/auth.js";
import { useToast } from "vue-toastification";
import ActivityForm from "./ActivityForm.vue";
import { useRouter } from "vue-router";
import { formatRupiah } from "@/utils/format";

const auth = useAuthStore();
const $loading = useLoading();
const selectedData = ref({});
const modal = ref(false);
const swal = inject("$swal");
const toast = useToast();
const router = useRouter();

const headers = ref([
    {
        key: "no",
        label: "No",
        thClass: "text-center px-1",
        tdClass: "text-center px-1",
    },
    {
        key: "name",
        label: "Program Name",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
        {
        key: "pax",
        label: "Pax",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
    {
        key: "from_destination_format",
        label: "From",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
    {
        key: "to_destination_format",
        label: "To",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
    {
        key: "price_format",
        label: "Price",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
    {
        key: "sub_total_format",
        label: "Total Price",
        sortable: true,
        thClass: "text-left",
        tdClass: "text-left",
    },
    {
        key: "accurate_status",
        label: "Accurate",
        thClass: "text-center",
        tdClass: "text-center",
    },
    { key: "action", label: "Action" },
]);
const customActions = ref([
    {
        label: "Sync Accurate",
        variant: "info",
        icon: "fas fa-sync",
        action: (item) => {
            syncToAccurate(item.uuid);
        },
        showIf: () => false,
    },

    {
        label: "Detail",
        variant: "primary",
        icon: "fas fa-info-circle",
        action: (item) => {
            if(item.uuid){
                router.push(`programs/detail/${item.uuid}`);
            }
        },
    },
    {
        label: "Edit",
        variant: "success",
        icon: "fas fa-edit",
        action: (item) => {
            if(item.uuid){
                router.push(`programs/edit/${item.uuid}`);
            }
        },
    },
        {
        label: "Duplicate",
        variant: "secondary",
        icon: "fas fa-copy",
        action: (item) => {
            swal({
                title: "Confirm",
                text: "Are you sure to duplicate this data?",
                showCancelButton: true,
                confirmButtonText: "Yes, duplicate!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-primary me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    duplicateData(item.uuid);
                } else {
                    swal.close();
                }
            });
        },
    },
    {
        label: "Delete",
        variant: "danger",
        icon: "fas fa-trash",
        action: (item) => {
            swal({
                title: "Confirm",
                text: "Are you sure to delete this data?",
                showCancelButton: true,
                confirmButtonText: "Yes, delete!",
                cancelButtonText: "Cancel",
                customClass: {
                    popup: "rounded",
                    confirmButton: "btn btn-danger me-2",
                    cancelButton: "btn btn-secondary",
                },
                buttonsStyling: false,
            }).then((confirm) => {
                if (confirm.value) {
                    deleteData(item.uuid);
                } else {
                    swal.close();
                }
            });
        },
    },
]);

const searchData = async (search) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll(search);
    } finally {
        loader.hide();
    }
};

const addProgram = () => {
    router.push('programs/add');
};

const updateModal = (newValue) => {
    modal.value = newValue;
};

let programList = ref([]);

const getAll = async (search = "") => {
    try {
        const response = await axios.get(
            `${process.env.VUE_APP_API_URL}/program`,
            {
                params: {
                    search: search,
                },
                headers: {
                    "Content-type": "application/json",
                    Authorization: "Bearer " + auth._token,
                },
            }
        );

        const res = response.data;

        if (res.status) {
            programList.value = res.data.map((i) => ({
                ...i,
                from_destination_format: i.from_destination ? `${i.from_destination.name} (${i.from_destination.code})` : '-',
                to_destination_format: i.to_destination ? `${i.to_destination.name} (${i.to_destination.code})` : '-',
                price_format: formatRupiah(i.price),
                sub_total_format: formatRupiah(i.sub_total),
                accurate_status: i.accurate_id ? 'Synced' : 'Pending',
            }));
        }
    } catch (error) {
        console.error("Error fetching programs:", error);
        toast.error("Failed to load programs");
    }
};

const duplicateData = async (uuid) => {
    if (uuid) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.post(
                `${process.env.VUE_APP_API_URL}/program/duplicate/${uuid}`, {},
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                toast.success(res.message);
                getAll();
            }
        } finally {
            loader.hide();
        }
    }
};

const deleteData = async (uuid) => {
    if (uuid) {
        toast.clear();
        const loader = $loading.show({
            color: "#009689",
            loader: "bars",
            zIndex: 999999,
        });

        try {
            const response = await axios.delete(
                `${process.env.VUE_APP_API_URL}/program/delete/${uuid}`,
                {
                    headers: {
                        "Content-type": "application/json",
                        Authorization: "Bearer " + auth._token,
                    },
                }
            );

            const res = response.data;

            if (res.status) {
                toast.success(res.message);
                getAll();
            }
        } finally {
            loader.hide();
        }
    }
};

const syncToAccurate = async (uuid) => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        const response = await axios.post(
            `${process.env.VUE_APP_API_URL}/program/sync/${uuid}`,
            {},
            {
                headers: {
                    "Content-type": "application/json",
                    Authorization: "Bearer " + auth._token,
                },
            }
        );

        const res = response.data;

        if (res.status) {
            toast.success("Program sync to Accurate dispatched!");
            getAll();
        }
    } catch (e) {
        toast.error(e.response?.data?.message ?? "Sync failed");
    } finally {
        loader.hide();
    }
};

onMounted(async () => {
    const loader = $loading.show({
        color: "#489464",
        loader: "spinner",
    });
    try {
        await getAll();
    } finally {
        loader.hide();
    }
});
</script>
